import styles from './index.module.css'

import { LuChevronDown } from '@react-icons/all-files/lu/LuChevronDown'
import { LuHeart } from '@react-icons/all-files/lu/LuHeart'
import React, { useMemo } from 'react'

import { Button, DropdownMenu, Flex, SearchTextField } from 'components/ds'

import type { FilterValue } from '../BrowseLibraries'
import EasyAccessSwitch from '../easy-access-switch'

export type FilterOption = {
  label: string
  value: FilterValue
  icon?: React.ReactNode
  className?: string
}

const DEFAULT_FILTER_OPTIONS: FilterOption[] = [
  {
    label: 'Favorites',
    value: 'is_favorite',
    icon: <LuHeart />,
    className: styles.favoriteItem,
  },
  { label: 'Browse all Catalogs', value: 'all' },
]

type Props = {
  query: string
  filter: FilterValue
  categories?: string[]
  onQueryChange: (_e: React.ChangeEvent<HTMLInputElement>) => void
  onFilterChange: (_value: FilterValue) => void
  onClearAll: () => void
  onClearQuery?: () => void
  onSearch: () => void
  isSearchPage?: boolean
  easyAccess?: boolean
  onEasyAccessChange?: (
    _event: React.SyntheticEvent<HTMLButtonElement> | boolean,
  ) => void
}

const DiscoverHeader = ({
  query,
  filter,
  categories = [],
  onQueryChange,
  onFilterChange,
  onClearAll,
  onClearQuery,
  onSearch,
  isSearchPage,
  easyAccess,
  onEasyAccessChange,
}: Props): React.ReactNode => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch()
    }
  }

  const filterOptions = useMemo(() => {
    const categoryOptions: FilterOption[] = categories.map(name => ({
      label: name,
      value: name,
    }))
    return [...DEFAULT_FILTER_OPTIONS, ...categoryOptions]
  }, [categories])

  const selectedFilter = filterOptions.find(opt => opt.value === filter)
  const filterLabel = selectedFilter ? selectedFilter.label : 'Filter by'

  return (
    <>
      <div className={styles.container}>
        <Flex wrap="wrap" gap="2" width="100%" align="center" minWidth="0">
          <div className={styles.searchFieldContainer}>
            <SearchTextField
              value={query}
              onChange={onQueryChange}
              onKeyDown={handleKeyDown}
              onClear={onClearQuery}
              placeholder="Find a catalog"
              iconPosition="right"
            />
          </div>
          <Button variant="solid" size="2" onClick={onSearch}>
            Search
          </Button>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <Button
                variant={selectedFilter ? 'solid' : 'outline'}
                size="2"
                className={styles.filterButton}
              >
                {filterLabel}
                <LuChevronDown />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {DEFAULT_FILTER_OPTIONS.map(option => (
                <DropdownMenu.CheckboxItem
                  key={option.value}
                  checked={filter === option.value}
                  onCheckedChange={() => onFilterChange(option.value)}
                  className={option.className}
                >
                  {option.label}
                  {option.icon}
                </DropdownMenu.CheckboxItem>
              ))}
              {categories.length > 0 && <DropdownMenu.Separator />}
              {categories.map(category => (
                <DropdownMenu.CheckboxItem
                  key={category}
                  checked={filter === category}
                  onCheckedChange={() => onFilterChange(category)}
                >
                  {category}
                </DropdownMenu.CheckboxItem>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
          <Button
            variant="ghost"
            size="2"
            onClick={onClearAll}
            className={styles.clearButton}
          >
            Clear all
          </Button>
          {isSearchPage && onEasyAccessChange && (
            <div className={styles.easyAccessContainer}>
              <EasyAccessSwitch
                onClick={onEasyAccessChange}
                value={easyAccess ?? false}
                label="Download access"
                tooltip="Download access means you can download tracks from the catalog."
              />
            </div>
          )}
        </Flex>
      </div>
      <div className={styles.spacer} />
    </>
  )
}

export default DiscoverHeader
